import {
  Badge,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { Debit } from 'interfaces';
import { useCurrentSiteStore } from 'store';
import { formatDateOnly, toISODate } from 'utils/dateUtils';
import { TariffBadge as CommonTariffBadge } from 'components/common/TariffBadge';
import { DateTime } from 'luxon';

interface DebitTimelineProps {
  data: Debit[];
  isLoading: boolean;
  isError: boolean;
}

interface DebitDateGroup {
  dateKey: string;
  label: string;
  debits: Debit[];
}

const formatCurrency = (amount: string | null | undefined) => {
  if (amount === null || amount === undefined || amount === '') {
    return 'Not provided';
  }

  const parsed = Number(amount);
  if (Number.isNaN(parsed)) {
    return amount;
  }

  return Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
  }).format(parsed);
};

const formatTariff = (debit: Debit) => {
  if (!debit.tariff) {
    return 'Not provided';
  }

  return `${debit.tariff.prefix}${debit.tariff.code}`;
};

const formatStatus = (status: string | null | undefined) => {
  if (!status) {
    return 'Unknown';
  }

  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const getStatusColor = (status: string | null | undefined) => {
  const normalizedStatus = status?.toLowerCase();

  if (normalizedStatus === 'processed') {
    return 'green';
  }

  if (normalizedStatus === 'pending') {
    return 'yellow';
  }

  return 'gray';
};

const PaymentTariffBadge: React.FC<{ debit: Debit }> = ({ debit }) => {
  const tariffLabel = formatTariff(debit);
  const description = debit.tariff?.description?.toLowerCase();
  const tariff = debit.tariff;

  return (
    <Tooltip
      label={description}
      disabled={!description}
      position="top"
      withArrow
      multiline
      maw={320}
    >
      <span style={{ display: 'inline-flex' }}>
        {tariff ? (
          <CommonTariffBadge
            code={tariff.code}
            prefix={tariff.prefix}
            type={tariff.type}
          />
        ) : (
          <Badge variant="filled" color="gray" autoContrast radius="sm">
            {tariffLabel}
          </Badge>
        )}
      </span>
    </Tooltip>
  );
};

const groupDebitsByCreatedAtDate = (
  debits: Debit[],
  timezone: string
): DebitDateGroup[] => {
  const groups = new Map<string, DebitDateGroup>();

  debits.forEach((debit) => {
    const dateKey = toISODate(debit.createdAt);
    const label = formatDateOnly(debit.createdAt, {
      format: DateTime.DATE_MED,
      timezone,
      fallback: 'Unknown payment date',
    });

    const existing = groups.get(dateKey);

    if (existing) {
      existing.debits.push(debit);
      return;
    }

    groups.set(dateKey, {
      dateKey,
      label,
      debits: [debit],
    });
  });

  return Array.from(groups.values());
};

const EobBadges: React.FC<{ debit: Debit }> = ({ debit }) => {
  const eobs = debit.eobs ?? [];

  if (eobs.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        None
      </Text>
    );
  }

  return (
    <Group gap={4} wrap="wrap">
      {eobs.map((eob) => (
        <Tooltip
          key={eob.id ?? eob.code}
          label={eob.description?.toLowerCase()}
          disabled={!eob.description}
          position="top"
          withArrow
          multiline
          maw={320}
        >
          <Badge variant="light" color="red" radius="sm">
            {eob.code}
          </Badge>
        </Tooltip>
      ))}
    </Group>
  );
};

export const DebitTimeline: React.FC<DebitTimelineProps> = ({
  data,
}: DebitTimelineProps) => {
  const { timezone } = useCurrentSiteStore();

  const groupedDebits = groupDebitsByCreatedAtDate(data, timezone);

  return (
    <Stack gap="lg" w="100%">
      {groupedDebits.map((group) => {
        return (
          <Paper
            key={group.dateKey}
            withBorder
            radius="md"
            p={0}
            style={{ overflow: 'hidden' }}
          >
            <Group
              justify="space-between"
              align="flex-start"
              wrap="wrap"
              px="md"
              py="sm"
            >
              <Stack gap={4}>
                <Text size="sm" c="dimmed" fw={500}>
                  {group.label}
                </Text>
                <Badge variant="light" color="gray" radius="sm">
                  {group.debits.length}{' '}
                  {group.debits.length === 1 ? 'record' : 'records'}
                </Badge>
              </Stack>
            </Group>

            <Table.ScrollContainer minWidth={620}>
              <Table
                highlightOnHover
                horizontalSpacing="sm"
                verticalSpacing="xs"
              >
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Tariff</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Requested</Table.Th>
                    <Table.Th>Paid</Table.Th>
                    <Table.Th>EOB</Table.Th>
                    <Table.Th>Microfilm</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {group.debits.map((debit) => (
                    <Table.Tr key={debit.id}>
                      <Table.Td>
                        <PaymentTariffBadge debit={debit} />
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          variant="light"
                          color={getStatusColor(debit.status)}
                          radius="sm"
                        >
                          {formatStatus(debit.status)}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {formatCurrency(debit.requestedAmount)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={600}>
                          {formatCurrency(debit.paidAmount)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <EobBadges debit={debit} />
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {debit.microfilm || 'Not provided'}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Paper>
        );
      })}
    </Stack>
  );
};
